"use client"

import Link from "next/link"
import Image from "next/image"
import { useState, useRef, useEffect } from "react"
import { UserAvatar } from "@/components/shared/UserAvatar"
import { UserMenu } from "@/components/navigation/UserMenu"
import { Button } from "@/components/ui/button"
import { ThemeToggle } from "@/components/shared/ThemeToggle"

type PublicNavbarProps = {
  user?: {
    name?: string | null
    email?: string | null
    image?: string | null
  } | null
}

export function PublicNavbar({ user }: PublicNavbarProps) {
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }

    if (userMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [userMenuOpen])

  return (
    <nav className="border-b border-border bg-background sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2">
            <div className="bg-blue-600 rounded-lg p-2.5">
              <Image 
                src="/logo.png" 
                alt="PadelManager Logo" 
                width={40} 
                height={40}
                className="object-contain"
              />
            </div>
            <span className="text-2xl font-bold text-green-600">PadelManager</span>
          </Link>

          {/* Navigation Links - Desktop */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              href="/tournaments" 
              className="text-muted-foreground hover:text-green-600 font-medium transition"
            >
              Tornei
            </Link>
            <Link
              href="/classifiche" 
              className="text-muted-foreground hover:text-green-600 font-medium transition"
            >
              Classifiche
            </Link>
            <Link
              href="/giocatori" 
              className="text-muted-foreground hover:text-green-600 font-medium transition"
            >
              Giocatori
            </Link>
            <Link
              href="/prezzi" 
              className="text-muted-foreground hover:text-green-600 font-medium transition"
            >
              Prezzi
            </Link>
            <Link
              href="/chi-siamo" 
              className="text-muted-foreground hover:text-green-600 font-medium transition"
            >
              Chi Siamo
            </Link>
          </div>

          {/* Auth / User Menu */}
          <div className="flex items-center gap-3">
            <ThemeToggle />
            {user ? (
              <>
                <Button asChild className="hidden sm:inline-flex bg-blue-600 hover:bg-blue-700">
                  <Link href="/dashboard">
                    Dashboard
                  </Link>
                </Button>
                
                {/* User Menu */}
                <div className="relative" ref={menuRef}>
                  <Button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    variant="ghost"
                    className="h-auto px-3 py-2"
                  >
                    <UserAvatar 
                      name={user.name || null} 
                      image={user.image || null} 
                      size="md" 
                    />
                    <div className="hidden sm:block text-left">
                      <div className="text-sm font-medium text-foreground">
                        {user.name || "Utente"}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {user.email}
                      </div>
                    </div>
                    <svg
                      className={`w-4 h-4 text-muted-foreground transition-transform ${
                        userMenuOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </Button>

                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-popover rounded-lg shadow-lg border border-border py-2 z-50">
                      <UserMenu onItemClick={() => setUserMenuOpen(false)} />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Button asChild className="bg-blue-600 hover:bg-blue-700 font-semibold">
                <Link href="/auth/signin">
                  Accedi
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}

